import React, { useEffect, useState } from "react";
import {
  sendDeleteRequest,
  sendGetRequest,
  sendPostRequest,
} from "../../utils";
import { DndProvider as MultiBackendProvider } from "react-dnd-multi-backend";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PeerReviewSuccess from "./PeerReviewSuccess";
import Toaster from "../Toaster";
import moment from "moment-timezone";
import Trophy from "../experience/Trophy";
import TraitSlot from "../experience/TraitSlot";
import { HTML5toTouch, TRAITS } from "../experience/constants";
import CustomDragLayer from "../experience/DragLayer";

moment.tz.setDefault("America/Toronto");

const trophies = ["trophy-1", "trophy-2", "trophy-3", "trophy-4", "trophy-5"];

const PeerReview = ({ user }) => {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [trophyPool, setTrophyPool] = useState(trophies);
  const [assignments, setAssignments] = useState({});
  const [successModal, setSuccessModal] = useState(null);
  const [peerReviews, setPeerReviews] = useState([]);
  const [myAccount, setMyAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const getLatestReviews = async () => {
    const resp = await sendGetRequest(`employees/peer-review`, true);
    return await resp.json();
  };

  // Load initial data
  useEffect(() => {
    sendGetRequest(`employees-peer-review`, true)
      .then((res) => res.text())
      .then(async (data) => {
        const parsedData = JSON.parse(data);
        setData(parsedData);

        const myAccount = parsedData.find(
          (emp) => emp.username === user.username
        );
        setMyAccount(myAccount);

        // Initialize assignments
        let init = {};
        parsedData.forEach((emp) => {
          init[emp.id] = {
            cleanliness: null,
            reliability: null,
            fun: null,
          };
        });

        // Load existing assignments
        try {
          const reviews = await getLatestReviews();
          setPeerReviews(reviews);

          // Update assignments with existing data
          let index = 1;
          reviews.forEach((review) => {
            if (init[review.employee_id]) {
              init[review.employee_id] = {
                cleanliness: review.cleanliness ? `trophy-${index++}` : null,
                reliability: review.reliability ? `trophy-${index++}` : null,
                fun: review.fun ? `trophy-${index++}` : null,
              };
            }
          });

          setAssignments(init);

          // Update trophy pool
          const assignedTrophies = new Set();
          Object.values(init).forEach((empTraits) => {
            Object.values(empTraits).forEach((trophyId) => {
              if (trophyId) assignedTrophies.add(trophyId);
            });
          });

          setTrophyPool(trophies.filter((t) => !assignedTrophies.has(t)));
        } catch (error) {
          setError("Failed to fetch peer reviews");
        }
        setIsLoading(false);
      })
      .catch((err) => setError(JSON.stringify(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragEnd = async (item, monitor) => {
    setError(null);
    setSuccessModal(null);

    if (!monitor.didDrop()) return;

    const dropResult = monitor.getDropResult();
    if (!dropResult) return;

    const { slotId } = dropResult;
    const [, empId, trait] = slotId.split("|");
    const trophyId = item.id;

    // Check if the slot already has a trophy
    if (assignments[empId] && assignments[empId][trait]) {
      return;
    }

    // Check if this is a self-review
    if (parseInt(empId) === myAccount?.id) {
      // Count how many trophies are already assigned to self
      const selfTrophies = Object.values(assignments[empId] || {}).filter(
        (t) => t !== null
      ).length;

      // If trying to assign more than one trophy to self, show error and return
      if (selfTrophies >= 1) {
        setError("Vous ne pouvez vous attribuer qu'un seul trophée.");
        return;
      }
    }

    const employee = data.find((emp) => emp.id === parseInt(empId));
    const trophyValue = parseInt(trophyId.replace("trophy-", ""));

    // Update UI
    setAssignments((prev) => {
      const updated = { ...prev };
      updated[empId][trait] = trophyId;
      return updated;
    });

    setTrophyPool((prev) => prev.filter((t) => t !== trophyId));

    // Send to server
    const resp = await sendPostRequest("employees/peer-review", {
      employee_id: parseInt(empId),
      trait,
    });

    if (resp.ok) {
      setSuccessModal({
        employee,
        trait,
        points: trophyValue,
      });
    } else {
      setError("Failed to assign trophy");
    }

    const reviews = await getLatestReviews();
    setPeerReviews(reviews);
  };

  const handleDeleteTrophy = async (empId, trait, trophy) => {
    // Update UI
    setAssignments((prev) => {
      const updated = { ...prev };
      updated[empId][trait] = null;
      return updated;
    });
    setTrophyPool((prev) => [...prev, trophy]);

    // Get review ID and delete from server
    const review = peerReviews.find(
      (review) => review.employee_id === parseInt(empId)
    );

    if (review) {
      try {
        await sendDeleteRequest(`employees/peer-review/${review.id}`, {
          trait,
        });
      } catch (error) {
        setError("Failed to remove trophy");
        // Revert changes
        setAssignments((prev) => {
          const updated = { ...prev };
          updated[empId][trait] = trophy;
          return updated;
        });
        setTrophyPool((prev) => prev.filter((t) => t !== trophy));
      }
    }

    const reviews = await getLatestReviews();
    setPeerReviews(reviews);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <CircularProgress />
      </div>
    );
  }

  return (
    <MultiBackendProvider
      options={HTML5toTouch}
      backends={HTML5toTouch.backends}
    >
      <CustomDragLayer />
      <div className="relative">
        <Box sx={{ paddingTop: 2, flexGrow: 1 }} className="">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography
                variant="h2"
                className="customer-list-header !font-semibold !text-xl text-center text-green-600 leading-tight"
              >
                Récompensez des collègues qui se sont démarqués aujourd'hui!
              </Typography>
              <div className="mt-2">
                <div
                  id="trophy-pool"
                  className="flex gap-4 rounded-lg  h-[56px] mx-auto justify-center"
                >
                  {trophyPool.map((id) => (
                    <Trophy
                      key={id}
                      id={id}
                      isAssigned={false}
                      onDragEnd={handleDragEnd}
                    />
                  ))}
                  {Array.from({
                    length: trophies.length - trophyPool.length,
                  }).map((_, index) => (
                    <div
                      key={index}
                      className={`bg-yellow-50 text-white p-2 rounded shadow w-[56px] overflow-hidden h-[56px] flex items-center justify-center border border-yellow-500 opacity-20 cursor-not-allowed`}
                    >
                      <span className="text-yellow-500 text-3xl self-center mx-auto">
                        🏆
                      </span>
                    </div>
                  ))}
                </div>
                <span className="text-sm text-center flex justify-center items-center mt-2 text-gray-500">
                  Glissez les trophés
                </span>
              </div>
              <div className="w-full max-w-md mx-auto py-0 mt-4 px-4 sm:px-0">
                <TableContainer className="max-h-[460px] rounded-xl shadow-lg [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 border-slate-300 border-t-[6px] border-b-[6px]">
                  <Table
                    stickyHeader
                    sx={{ minWidth: 350 }}
                    aria-label="employees"
                  >
                    <TableHead className="">
                      <TableRow className="">
                        <TableCell className="!text-center">&nbsp;</TableCell>
                        {TRAITS.map((trait) => (
                          <TableCell key={trait} className="!text-center !p-2">
                            <div
                              className={`p-2 rounded-lg items-center justify-center flex ${
                                trait === "cleanliness" && "bg-green-500/20"
                              } ${
                                trait === "reliability" && "bg-blue-500/20"
                              } ${trait === "fun" && "bg-orange-500/20"}`}
                            >
                              {trait === "cleanliness" && (
                                <img
                                  src="/mop.png"
                                  alt="mop"
                                  className="w-6 h-6"
                                />
                              )}
                              {trait === "reliability" && (
                                <img
                                  src="/flash.png"
                                  alt="wrench"
                                  className="w-6 h-6"
                                />
                              )}
                              {trait === "fun" && (
                                <img
                                  src="/happiness.png"
                                  alt="party"
                                  className="w-6 h-6"
                                />
                              )}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.map((employee) => (
                        <TableRow
                          key={employee.id}
                          className=" bg-white p-6 text-center 0 my-6 mx-6"
                        >
                          <TableCell className="flex-none !text-center flex !p-2 !border-slate-200/70">
                            <div className="flex flex-col justify-center items-center relative">
                              <img
                                src={
                                  employee.photo
                                    ? `${process.env.REACT_APP_DOMAIN}/uploads/employees/${employee.photo}`
                                    : "/default-avatar.png"
                                }
                                alt={employee.name}
                                className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg  object-cover border border-gray-200"
                              />
                              <span className="flex w-full py-[1px] text-center justify-center rounded-t-lg shadow text-sm absolute -bottom-[9px] bg-slate-400 text-white">
                                {employee.first_name}
                              </span>
                            </div>
                          </TableCell>
                          {assignments[employee.id] &&
                            TRAITS.map((trait) => (
                              <TraitSlot
                                key={trait}
                                id={`slot|${employee.id}|${trait}`}
                                trophy={assignments[employee.id][trait]}
                                label={trait}
                                onDelete={handleDeleteTrophy}
                              />
                            ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </Grid>
          </Grid>
        </Box>
      </div>
      <PeerReviewSuccess
        openConfirmation={!!successModal}
        setOpenConfirmation={setSuccessModal}
        onAction={() => setSuccessModal(null)}
        data={successModal}
        user={user}
      />
      <Toaster
        open={!!error}
        onClose={() => setError(null)}
        message={error}
        severity="error"
      />
    </MultiBackendProvider>
  );
};

export default PeerReview;
